import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import api from "../lib/api";
import { trackClick } from "../lib/tracking";
import { Grid3x3, Clock, MapPin, BookOpen, Lightbulb, Sword, ArrowRight, Calendar, Mic, Trophy, Baby, Users, Map as MapIcon } from "lucide-react";
import { Link } from "react-router-dom";

// Map zone icon string -> emoji or letter (since Ionicons not avail here)
const ZONE_ICON_EMOJI = {
  dice: "🎲",
  library: "📚",
  mic: "🎙️",
  resize: "🧩",
  trophy: "🏆",
  construct: "🛠️",
  "extension-puzzle": "🧩",
  happy: "🧒",
  flag: "🚩",
  people: "🎭",
  albums: "🃏",
  restaurant: "🍽️",
};

export default function ZonesPage() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/zones")
      .then((r) => setZones(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-full" style={{ backgroundColor: "#F0F6FF" }}>
      <PageHeader icon={Grid3x3} title="Strefy" subtitle="Poznaj wszystkie strefy festiwalu" />
      <div className="px-4 sm:px-6 md:px-10 py-8">
        {loading ? (
          <div className="text-center py-10" style={{ color: "#5A7A9A" }}>Ładowanie...</div>
        ) : (
          <div className="grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="zones-grid">
            {zones.map((z) => (
              <div
                key={z.id}
                onClick={() => trackClick("zone", z.id, z.name)}
                className="rounded-[16px] overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #E0ECF5",
                  boxShadow: "0 2px 8px rgba(0,73,168,0.08)",
                }}
                data-testid={`zone-card-${z.id}`}
              >
                <div
                  className="flex items-center gap-3 px-5 py-4"
                  style={{
                    background: "linear-gradient(135deg, #0049A8 0%, #003580 100%)",
                  }}
                >
                  <div
                    className="flex items-center justify-center text-2xl"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      backgroundColor: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(0,176,176,0.4)",
                    }}
                  >
                    {ZONE_ICON_EMOJI[z.icon] || "✨"}
                  </div>
                  <div style={{ color: "#fff", fontSize: 17, fontWeight: 800, lineHeight: 1.2, wordBreak: "break-word", overflowWrap: "anywhere", minWidth: 0 }} className="flex-1">
                    {z.name}
                  </div>
                </div>
                <div className="px-5 py-4 flex flex-col gap-3">
                  <p style={{ color: "#1A365D", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-line", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    {z.description}
                  </p>

                  <div className="flex items-start gap-2">
                    <MapPin size={14} color="#00B0B0" className="flex-shrink-0 mt-0.5" />
                    <span style={{ color: "#5A7A9A", fontSize: 12, fontWeight: 600, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {z.location}
                    </span>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <div
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: "#E8F4FF", color: "#0049A8" }}
                    >
                      <Clock size={12} /> Sob: {z.hoursSaturday}
                    </div>
                    <div
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: "#FFF4E6", color: "#D45500" }}
                    >
                      <Clock size={12} /> Nd: {z.hoursSunday}
                    </div>
                  </div>

                  {z.features?.length > 0 && (
                    <ul className="flex flex-col gap-1 mt-1 pt-3" style={{ borderTop: "1px dashed #E0ECF5" }}>
                      {z.features.map((f, i) => (
                        <li key={`${z.id}-feat-${i}-${f.slice(0, 16)}`} className="flex items-start gap-2">
                          <span style={{ color: "#FF9900", fontWeight: 700 }}>•</span>
                          <span style={{ color: "#1A365D", fontSize: 12 }}>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {z.id === "rental" && (
                    <Link
                      to="/katalog-gier"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#0049A8", color: "#fff", fontSize: 13 }}
                      data-testid="zone-games-catalog-btn"
                    >
                      <BookOpen size={15} /> Katalog gier <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "prototypes" && (
                    <Link
                      to="/prototypy"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-prototypes-list-btn"
                    >
                      <Lightbulb size={15} /> Lista prototypów <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "wargames" && (
                    <Link
                      to="/gry-bitewne"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#7B2CBF", color: "#fff", fontSize: 13 }}
                      data-testid="zone-wargames-list-btn"
                    >
                      <Sword size={15} /> Lista gier <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "rpg" && (
                    <Link
                      to="/wydarzenia?type=rpg"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-rpg-sessions-btn"
                    >
                      <Calendar size={15} /> Sesje <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "lectures" && (
                    <Link
                      to="/wydarzenia?type=prelekcja"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-lectures-list-btn"
                    >
                      <Mic size={15} /> Prelekcje <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "tournaments" && (
                    <Link
                      to="/wydarzenia?type=turniej"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-tournaments-list-btn"
                    >
                      <Trophy size={15} /> Turnieje <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "kids" && (
                    <Link
                      to="/wydarzenia?type=najmłodsi"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-kids-events-btn"
                    >
                      <Baby size={15} /> Zaplanowane Wydarzenia <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "painting" && (
                    <Link
                      to="/wydarzenia?type=larp"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-larp-events-btn"
                    >
                      <Calendar size={15} /> Sesje LARP <ArrowRight size={14} />
                    </Link>
                  )}
                  {z.id === "bigformat" && (
                    <Link
                      to="/gry-wieloosobowe"
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#FF9900", color: "#fff", fontSize: 13 }}
                      data-testid="zone-bigformat-list-btn"
                    >
                      <Users size={15} /> Lista gier <ArrowRight size={14} />
                    </Link>
                  )}
                  {(z.buildingId || z.floorId || z.pinId) && (
                    <Link
                      to={`/mapa?${new URLSearchParams({
                        ...(z.buildingId ? { buildingId: z.buildingId } : {}),
                        ...(z.floorId ? { floorId: z.floorId } : {}),
                        ...(z.pinId ? { pinId: z.pinId } : {}),
                        t: String(Date.now()),
                      }).toString()}`}
                      className="flex items-center justify-center gap-2 mt-2 py-[10px] rounded-[10px] font-bold transition-transform hover:scale-[1.02]"
                      style={{ backgroundColor: "#0049A8", color: "#fff", fontSize: 13 }}
                      data-testid={`zone-${z.id}-map-btn`}
                    >
                      <MapIcon size={15} /> Pokaż na mapie <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
